import { Helmet } from 'react-helmet-async';
import { ArticleLang } from '../types/article';
import { BUSINESS, absoluteUrl } from '../services/seo';

interface SeoHeadProps {
  lang: ArticleLang;
  title: string;
  description: string;
  /** Path (no domain) of this page, e.g. "/ro" or "/hu/articles/some-slug". */
  path: string;
  /** Equivalent path per language, for hreflang. Omit a language if no translation exists. */
  alternates: Partial<Record<ArticleLang, string>>;
  type?: 'website' | 'article';
  jsonLd?: object[];
}

/**
 * Sets <title>/<meta description>/canonical/hreflang/OG/Twitter tags during
 * client-side navigation. This complements — does not replace —
 * scripts/build-seo.js, which bakes the same values into the static HTML
 * that non-JS crawlers receive. Both derive from src/services/seo.ts so the
 * two never disagree.
 */
export const SeoHead: React.FC<SeoHeadProps> = ({
  lang,
  title,
  description,
  path,
  alternates,
  type = 'website',
  jsonLd = [],
}) => {
  const url = absoluteUrl(path);
  const ogLocale = { ro: 'ro_RO', hu: 'hu_HU', en: 'en_US' }[lang];
  const imageUrl = absoluteUrl('/LogoKT.png');

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {Object.entries(alternates).map(([altLang, altPath]) => (
        <link
          key={altLang}
          rel="alternate"
          hrefLang={altLang}
          href={absoluteUrl(altPath as string)}
        />
      ))}
      {alternates.ro && <link rel="alternate" hrefLang="x-default" href={absoluteUrl(alternates.ro)} />}

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:site_name" content={BUSINESS.name} />
      <meta property="og:image" content={imageUrl} />

      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {jsonLd.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SeoHead;
